import React from 'react';
import { cn } from '../../theme/utils';
import SkeletonComponent, { 
  SkeletonText, 
  SkeletonAvatar, 
  SkeletonButton,
  SkeletonShimmer,
  LoadingPulse
} from '../Common/Skeleton';

// Use the default export as Skeleton
const Skeleton = SkeletonComponent;

/**
 * Enhanced Loading Spinner with context
 */
export const LoadingSpinner = ({ 
  size = 'md', 
  text = 'Loading...', 
  showText = true,
  className = '',
  color = 'primary'
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    primary: 'border-primary-500',
    white: 'border-white',
    neutral: 'border-neutral-400'
  };

  return (
    <div className={cn('flex flex-col items-center justify-center space-y-3', className)}>
      <div className={cn(
        'animate-spin rounded-full border-b-2',
        sizeClasses[size],
        colorClasses[color]
      )} />
      {showText && text && (
        <p className="text-neutral-400 text-sm animate-pulse">{text}</p>
      )}
    </div>
  );
};

/**
 * Current Player Auction Loading State
 */
export const CurrentPlayerAuctionLoader = () => {
  return (
    <div className="text-center py-8 space-y-6">
      <LoadingPulse>
        <div className="flex flex-col sm:flex-row items-center justify-center sm:space-x-8 space-y-4 sm:space-y-0">
          <SkeletonShimmer className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" />
          <div className="text-center sm:text-left space-y-3">
            <SkeletonShimmer className="h-6 sm:h-8 w-48 rounded-lg" />
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <SkeletonShimmer className="h-6 w-24 rounded-full" />
              <SkeletonShimmer className="h-4 w-20 rounded" />
            </div>
          </div>
        </div>
      </LoadingPulse>

      {/* Bid and Timer Loading */}
      <LoadingPulse>
        <div className="flex items-center justify-center space-x-4 sm:space-x-8">
          <div className="text-center space-y-2">
            <SkeletonShimmer className="h-8 sm:h-10 w-20 sm:w-24 rounded-lg mx-auto" />
            <SkeletonText className="h-3 w-16 mx-auto" />
          </div>
          <div className="text-center space-y-2">
            <SkeletonShimmer className="h-8 sm:h-10 w-16 sm:w-20 rounded-lg mx-auto" />
            <SkeletonText className="h-3 w-14 mx-auto" />
          </div>
        </div>
      </LoadingPulse>

      {/* Bidding Interface Loading */}
      <LoadingPulse>
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <SkeletonShimmer className="flex-1 h-12 rounded-lg" />
            <SkeletonShimmer className="w-full sm:w-32 h-12 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonShimmer key={index} className="h-8 rounded-lg" />
            ))}
          </div>
        </div>
      </LoadingPulse>
    </div>
  );
};

/**
 * My Squad Loading State
 */
export const MySquadLoader = () => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SkeletonText className="h-6 w-24" />
        <SkeletonText className="h-4 w-20" />
      </div>
      
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <LoadingPulse key={index}>
            <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-800">
              <div className="flex items-center space-x-3">
                <SkeletonShimmer className="w-8 h-8 rounded-full" />
                <div className="space-y-1">
                  <SkeletonText className="h-4 w-24" />
                  <SkeletonText className="h-3 w-16" />
                </div>
              </div>
              <SkeletonText className="h-4 w-12" />
            </div>
          </LoadingPulse>
        ))}
      </div>
      
      <LoadingPulse>
        <div className="pt-4 border-t border-neutral-700 space-y-2">
          <div className="flex justify-between">
            <SkeletonText className="h-4 w-20" />
            <SkeletonText className="h-4 w-16" />
          </div>
          <div className="flex justify-between">
            <SkeletonText className="h-4 w-24" />
            <SkeletonText className="h-4 w-20" />
          </div>
        </div>
      </LoadingPulse>
    </div>
  );
};

/**
 * Recent Activity Loading State
 */
export const RecentActivityLoader = () => {
  return (
    <div className="space-y-4">
      <SkeletonText className="h-6 w-32" />
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <LoadingPulse key={index}>
            <div className="space-y-2">
              <SkeletonText className="h-4 w-full" />
              <SkeletonText className="h-3 w-2/3" />
              <SkeletonText className="h-3 w-16" />
            </div>
          </LoadingPulse>
        ))}
      </div>
    </div>
  );
};

/**
 * Chat Loading State
 */
export const ChatLoader = () => {
  return (
    <div className="space-y-4">
      <SkeletonText className="h-6 w-16" />
      
      {/* Chat Messages */}
      <div className="space-y-4 max-h-64 overflow-y-auto">
        {Array.from({ length: 6 }).map((_, index) => (
          <LoadingPulse key={index}>
            <div className={cn(
              'flex space-x-3',
              index % 3 === 0 && 'flex-row-reverse space-x-reverse'
            )}>
              <SkeletonShimmer className="w-8 h-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <SkeletonText className="h-3 w-20" />
                <div className={cn(
                  'rounded-lg p-3',
                  index % 3 === 0 ? 'bg-primary-500' : 'bg-neutral-800'
                )}>
                  <SkeletonText 
                    className="h-4" 
                    lines={Math.floor(Math.random() * 2) + 1} 
                  />
                </div>
              </div>
            </div>
          </LoadingPulse>
        ))}
      </div>
      
      {/* Chat Input */}
      <LoadingPulse>
        <div className="flex space-x-2 pt-4 border-t border-neutral-700">
          <SkeletonShimmer className="flex-1 h-10 rounded-lg" />
          <SkeletonShimmer className="w-16 h-10 rounded-lg" />
        </div>
      </LoadingPulse>
    </div>
  );
};

/**
 * Player Filters Loading State
 */
export const PlayerFiltersLoader = () => {
  return (
    <LoadingPulse>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="space-y-2">
          <SkeletonText className="h-4 w-16" />
          <SkeletonShimmer className="h-10 rounded-lg" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-20" />
          <SkeletonShimmer className="h-10 rounded-lg" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-12" />
          <SkeletonShimmer className="h-10 rounded-lg" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-16" />
          <SkeletonShimmer className="h-10 rounded-lg" />
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="mb-4">
        <SkeletonShimmer className="h-10 rounded-lg" />
      </div>
    </LoadingPulse>
  );
};

/**
 * Draft Header Loading State
 */
export const DraftHeaderLoader = () => {
  return (
    <LoadingPulse>
      <div className="bg-neutral-900 border-b border-neutral-800 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <SkeletonShimmer className="w-12 h-12 rounded-full" />
              <div className="space-y-2">
                <SkeletonText className="h-6 w-48" />
                <SkeletonText className="h-4 w-32" />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              {/* Stats */}
              <div className="flex space-x-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="text-center">
                    <SkeletonText className="h-6 w-12 mx-auto" />
                    <SkeletonText className="h-3 w-16 mx-auto" />
                  </div>
                ))}
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-2">
                <SkeletonButton className="w-24 h-8" />
                <SkeletonButton className="w-8 h-8" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadingPulse>
  );
};

/**
 * League Card Loading State
 */
export const LeagueCardLoader = () => {
  return (
    <LoadingPulse>
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <SkeletonText className="h-6 w-32" />
              <SkeletonText className="h-4 w-24" />
            </div>
            <SkeletonShimmer className="w-12 h-6 rounded-full" />
          </div>
          
          <div className="grid grid-cols-3 gap-4 py-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="text-center">
                <SkeletonText className="h-6 w-8 mx-auto" />
                <SkeletonText className="h-3 w-12 mx-auto" />
              </div>
            ))}
          </div>
          
          <div className="flex space-x-2">
            <SkeletonButton className="flex-1 h-10" />
            <SkeletonButton className="w-20 h-10" />
          </div>
        </div>
      </div>
    </LoadingPulse>
  );
};

/**
 * Progressive Loading Wrapper
 */
export const ProgressiveLoader = ({ 
  isLoading, 
  skeleton, 
  children, 
  fallback,
  delay = 0,
  className = ''
}) => {
  const [showSkeleton, setShowSkeleton] = React.useState(true);

  React.useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setShowSkeleton(false), delay);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading, delay]);

  if (showSkeleton) {
    return skeleton || fallback || <LoadingSpinner />;
  }

  return <div className={className}>{children}</div>;
};

/**
 * Data Loading States
 */
export const DataLoader = ({ 
  loading, 
  error, 
  data, 
  skeleton, 
  errorComponent,
  emptyComponent,
  children,
  retryAction
}) => {
  if (loading) {
    return skeleton || <LoadingSpinner text="Loading data..." />;
  }

  if (error) {
    return errorComponent || (
      <div className="text-center py-8">
        <div className="text-red-400 mb-4">Failed to load data</div>
        {retryAction && (
          <button 
            onClick={retryAction}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (!data || (Array.isArray(data) && data.length === 0)) {
    return emptyComponent || (
      <div className="text-center py-8 text-neutral-400">
        No data available
      </div>
    );
  }

  return children;
};

export default {
  LoadingSpinner,
  CurrentPlayerAuctionLoader,
  MySquadLoader,
  RecentActivityLoader,
  ChatLoader,
  PlayerFiltersLoader,
  DraftHeaderLoader,
  LeagueCardLoader,
  ProgressiveLoader,
  DataLoader
};